if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.slider = null;
        this.slides = [];
        this.currentIndex = 0;
        this.counter = null;
        this.prevBtn = null;
        this.nextBtn = null;
        this.observer = null;
        this.sliderBound = false;
        this.zoomed = false;
        this.zoomScale = 2.5;
        this._panOriginX = 50;
        this._panOriginY = 50;
        this._panRafId = 0;
        this._panDirty = false;
        this._velX = 0;
        this._velY = 0;
        this._prevMoveT = 0;
        this._prevMoveOX = 0;
        this._prevMoveOY = 0;
      }

      _getImg(slide) {
        if (!slide) return null;
        if (slide.tagName === 'IMG') return slide;
        return slide.querySelector('img');
      }

      // Write origin to DOM in next rAF — no delay, just batching
      _panCommit() {
        if (this._panDirty) return;
        this._panDirty = true;
        requestAnimationFrame(() => {
          this._panDirty = false;
          const slide = this.slides[this.currentIndex];
          const img = slide ? this._getImg(slide) : null;
          if (img) img.style.transformOrigin = `${this._panOriginX}% ${this._panOriginY}%`;
        });
      }

      // Set origin directly (1:1 tracking) and track velocity
      _panMoveTo(x, y) {
        const now = performance.now();
        const dt = now - this._prevMoveT;
        if (dt > 0 && dt < 100) {
          // Exponential moving average for smooth velocity
          const a = 0.4;
          this._velX = a * ((x - this._prevMoveOX) / dt) + (1 - a) * this._velX;
          this._velY = a * ((y - this._prevMoveOY) / dt) + (1 - a) * this._velY;
        }
        this._prevMoveT = now;
        this._prevMoveOX = x;
        this._prevMoveOY = y;
        this._panOriginX = x;
        this._panOriginY = y;
        this._panCommit();
      }

      // Momentum: gentle coast like iOS Photos — heavy friction, short travel
      _startMomentum() {
        this._stopMomentum();
        // Cap initial velocity so it never flies
        const maxVel = 0.15;
        this._velX = Math.max(-maxVel, Math.min(maxVel, this._velX));
        this._velY = Math.max(-maxVel, Math.min(maxVel, this._velY));
        if (Math.abs(this._velX) < 0.005 && Math.abs(this._velY) < 0.005) {
          this._snapEdge();
          return;
        }
        let lastT = performance.now();
        const tick = (now) => {
          const dt = now - lastT;
          lastT = now;
          const friction = Math.pow(0.90, dt / 16);
          this._velX *= friction;
          this._velY *= friction;
          let nx = this._panOriginX + this._velX * dt;
          let ny = this._panOriginY + this._velY * dt;
          // Rubber-band at edges
          if (nx < 0) { nx *= 0.4; this._velX *= 0.6; }
          else if (nx > 100) { nx = 100 + (nx - 100) * 0.4; this._velX *= 0.6; }
          if (ny < 0) { ny *= 0.4; this._velY *= 0.6; }
          else if (ny > 100) { ny = 100 + (ny - 100) * 0.4; this._velY *= 0.6; }
          this._panOriginX = nx;
          this._panOriginY = ny;
          this._panCommit();
          if (Math.abs(this._velX) < 0.003 && Math.abs(this._velY) < 0.003) {
            this._panRafId = 0;
            this._snapEdge();
            return;
          }
          this._panRafId = requestAnimationFrame(tick);
        };
        this._panRafId = requestAnimationFrame(tick);
      }

      _stopMomentum() {
        if (this._panRafId) { cancelAnimationFrame(this._panRafId); this._panRafId = 0; }
      }

      // Spring back from overshoot
      _snapEdge() {
        const ox = this._panOriginX, oy = this._panOriginY;
        if (ox >= 0 && ox <= 100 && oy >= 0 && oy <= 100) return;
        const tx = Math.max(0, Math.min(100, ox));
        const ty = Math.max(0, Math.min(100, oy));
        const spring = () => {
          const dx = tx - this._panOriginX;
          const dy = ty - this._panOriginY;
          if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
            this._panOriginX = tx; this._panOriginY = ty;
            this._panCommit(); this._panRafId = 0; return;
          }
          this._panOriginX += dx * 0.15;
          this._panOriginY += dy * 0.15;
          this._panCommit();
          this._panRafId = requestAnimationFrame(spring);
        };
        this._panRafId = requestAnimationFrame(spring);
      }

      show(opener) {
        super.show(opener);
        this.initSlider();
        this.showActiveMedia();
      }

      hide() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.resetZoom();
        super.hide();
      }

      initSlider() {
        this.slider = this.querySelector('.product-media-modal__slider');
        this.counter = this.querySelector('.product-media-modal__counter');
        this.prevBtn = this.querySelector('.product-media-modal__nav--prev');
        this.nextBtn = this.querySelector('.product-media-modal__nav--next');

        if (!this.slider) return;
        this.slides = Array.from(this.slider.children);

        if (!this.sliderBound) {
          this.sliderBound = true;

          // Prevent modal close on slider/nav/header clicks
          this.slider.addEventListener('pointerup', (e) => e.stopPropagation());
          if (this.prevBtn) this.prevBtn.addEventListener('pointerup', (e) => e.stopPropagation());
          if (this.nextBtn) this.nextBtn.addEventListener('pointerup', (e) => e.stopPropagation());
          const header = this.querySelector('.product-media-modal__header');
          if (header) header.addEventListener('pointerup', (e) => e.stopPropagation());

          // Nav buttons
          if (this.prevBtn) {
            this.prevBtn.addEventListener('click', (e) => { e.preventDefault(); this.navigatePrev(); });
          }
          if (this.nextBtn) {
            this.nextBtn.addEventListener('click', (e) => { e.preventDefault(); this.navigateNext(); });
          }

          // Keyboard
          this.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); this.navigatePrev(); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); this.navigateNext(); }
          });

          // ===========================================
          // DOUBLE-TAP ZOOM via click events
          // With touch-action: manipulation, click fires
          // without 300ms delay on mobile.
          // Double-tap zoom: attach to each image directly
          // (scroll-snap containers may swallow click events)
          // ===========================================
          let lastClickTime = 0;
          const attachZoomListener = (img) => {
            img.addEventListener('click', (e) => {
              const now = Date.now();
              const dt = now - lastClickTime;
              lastClickTime = now;
              if (dt < 400 && dt > 0) {
                e.preventDefault();
                e.stopPropagation();
                lastClickTime = 0;
                this.toggleZoom(e.clientX, e.clientY);
              }
            });
          };
          this.slides.forEach((slide) => {
            const img = this._getImg(slide);
            if (img) attachZoomListener(img);
          });

          // ===========================================
          // DESKTOP: mouse drag pan while zoomed
          // ===========================================
          let mouseDown = false, mouseMoved = false;
          let mouseStartX = 0, mouseStartY = 0;
          let mouseStartOriginX = 50, mouseStartOriginY = 50;

          this.slider.addEventListener('mousedown', (e) => {
            if (!this.zoomed || e.button !== 0) return;
            e.preventDefault();
            this._stopMomentum();
            mouseDown = true;
            mouseMoved = false;
            mouseStartX = e.clientX;
            mouseStartY = e.clientY;
            mouseStartOriginX = this._panOriginX;
            mouseStartOriginY = this._panOriginY;
            this.slider.style.cursor = 'grabbing';
          });

          window.addEventListener('mousemove', (e) => {
            if (!mouseDown || !this.zoomed) return;
            e.preventDefault();
            mouseMoved = true;

            const slide = this.slides[this.currentIndex];
            const img = slide ? this._getImg(slide) : null;
            if (!img) return;

            const dx = e.clientX - mouseStartX;
            const dy = e.clientY - mouseStartY;
            const displayWidth = img.offsetWidth || 300;
            const displayHeight = img.offsetHeight || 300;
            const boost = 1.8;
            const pctPerPxX = ((100 / this.zoomScale) / displayWidth) * boost;
            const pctPerPxY = ((100 / this.zoomScale) / displayHeight) * boost;

            const nx = Math.max(0, Math.min(100, mouseStartOriginX - dx * pctPerPxX));
            const ny = Math.max(0, Math.min(100, mouseStartOriginY - dy * pctPerPxY));
            this._panMoveTo(nx, ny);
          });

          window.addEventListener('mouseup', (e) => {
            if (!mouseDown) return;
            mouseDown = false;
            this._startMomentum();
            this.slider.style.cursor = '';
            // If mouse didn't move, it was a click — let click handler deal with it
            // If mouse moved, suppress the next click to prevent accidental zoom toggle
            if (mouseMoved) {
              const suppress = (ev) => { ev.stopImmediatePropagation(); ev.preventDefault(); };
              this.slider.addEventListener('click', suppress, { capture: true, once: true });
            }
          });

          // Show grab cursor when hovering over zoomed image
          this.slider.addEventListener('mouseenter', () => {
            if (this.zoomed) this.slider.style.cursor = 'grab';
          });
          this.slider.addEventListener('mouseleave', () => {
            this.slider.style.cursor = '';
          });

          // ===========================================
          // UNIFIED TOUCH: pinch zoom + pan + double-tap
          // Single set of handlers to avoid conflicts
          // ===========================================
          let pinchStartDist = 0, pinchStartScale = 1;
          let pinchCenterX = 0, pinchCenterY = 0;
          let panStartX = 0, panStartY = 0;
          let panStartOriginX = 50, panStartOriginY = 50;
          let panActive = false;
          let panTotalDx = 0, panTotalDy = 0;
          let lastZoomedTapTime = 0;
          let panStartedAtEdge = 0; // -1=left edge, 1=right edge, 0=not at edge

          // ===========================================
          // SWIPE DOWN TO DISMISS (iOS Photos style)
          // Only active when NOT zoomed
          // ===========================================
          let dismissStartY = 0, dismissStartX = 0;
          let dismissActive = false;
          let dismissLocked = false; // true = confirmed vertical swipe

          this.slider.addEventListener('touchstart', (e) => {
            const count = e.touches.length;

            // Track for dismiss gesture (non-zoomed, single finger)
            if (count === 1 && !this.zoomed) {
              dismissStartX = e.touches[0].clientX;
              dismissStartY = e.touches[0].clientY;
              dismissActive = true;
              dismissLocked = false;
            }

            // --- PINCH START (2+ fingers) ---
            if (count >= 2) {
              e.preventDefault();
              dismissActive = false;
              panActive = false; // cancel any pan
              this._pinching = true;
              this.slider.style.overflowX = 'hidden';
              this.slider.style.scrollSnapType = 'none';
              const t0 = e.touches[0], t1 = e.touches[1];
              pinchStartDist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
              pinchStartScale = this.zoomed ? this.zoomScale : 1;
              pinchCenterX = (t0.clientX + t1.clientX) / 2;
              pinchCenterY = (t0.clientY + t1.clientY) / 2;
              return;
            }

            // --- PAN START (1 finger, zoomed only) ---
            if (count === 1 && this.zoomed && !this._pinching) {
              e.preventDefault();
              this._stopMomentum();
              panActive = true;
              panTotalDx = 0;
              panTotalDy = 0;
              panStartX = e.touches[0].clientX;
              panStartY = e.touches[0].clientY;
              panStartOriginX = this._panOriginX;
              panStartOriginY = this._panOriginY;
              // Only allow edge navigation if ALREADY at the edge when touch starts
              panStartedAtEdge = this._panOriginX <= 3 ? -1 : this._panOriginX >= 97 ? 1 : 0;
            }
          }, { passive: false });

          this.slider.addEventListener('touchmove', (e) => {
            const count = e.touches.length;

            // --- PINCH MOVE ---
            if (count >= 2) {
              if (!this._pinching) {
                this._pinching = true;
                panActive = false;
                this.slider.style.overflowX = 'hidden';
                this.slider.style.scrollSnapType = 'none';
                const t0 = e.touches[0], t1 = e.touches[1];
                pinchStartDist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
                pinchStartScale = this.zoomed ? this.zoomScale : 1;
                pinchCenterX = (t0.clientX + t1.clientX) / 2;
                pinchCenterY = (t0.clientY + t1.clientY) / 2;
              }
              e.preventDefault();
              const t0 = e.touches[0], t1 = e.touches[1];
              const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
              const ratio = dist / (pinchStartDist || 1);
              let newScale = pinchStartScale * ratio;
              newScale = Math.max(1, Math.min(10, newScale));

              const slide = this.slides[this.currentIndex];
              const img = slide ? this._getImg(slide) : null;
              if (!img) return;

              if (!this.zoomed) {
                const rect = img.getBoundingClientRect();
                const ox = Math.max(0, Math.min(100, ((pinchCenterX - rect.left) / rect.width) * 100));
                const oy = Math.max(0, Math.min(100, ((pinchCenterY - rect.top) / rect.height) * 100));
                img.style.transformOrigin = `${ox}% ${oy}%`;
                this._panOriginX = ox;
                this._panOriginY = oy;
              }

              img.style.transform = `scale(${newScale})`;
              this.zoomScale = newScale;
              if (newScale > 1.05) this.zoomed = true;
              return;
            }

            // --- DISMISS MOVE (1 finger, not zoomed) ---
            if (count === 1 && !this.zoomed && dismissActive && !this._pinching) {
              const dx = e.touches[0].clientX - dismissStartX;
              const dy = e.touches[0].clientY - dismissStartY;
              // First move: decide if this is vertical or horizontal
              if (!dismissLocked && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
                if (Math.abs(dy) > Math.abs(dx) && dy > 0) {
                  dismissLocked = true; // vertical downward confirmed
                  e.preventDefault();
                  this.slider.style.overflowX = 'hidden';
                  this.slider.style.scrollSnapType = 'none';
                } else {
                  dismissActive = false; // horizontal swipe — let native scroll handle it
                }
              }
              if (dismissLocked) {
                e.preventDefault();
                const progress = Math.max(0, dy) / (window.innerHeight * 0.4);
                const scale = Math.max(0.6, 1 - progress * 0.4);
                const opacity = Math.max(0.2, 1 - progress * 0.8);
                this.slider.style.transform = `translateY(${dy}px) scale(${scale})`;
                this.slider.style.transition = '';
                this.style.backgroundColor = `rgba(0, 0, 0, ${opacity * 0.95})`;
              }
              return;
            }

            // --- PAN MOVE (1 finger, zoomed) ---
            if (count === 1 && this.zoomed && panActive && !this._pinching) {
              e.preventDefault();
              const dx = e.touches[0].clientX - panStartX;
              const dy = e.touches[0].clientY - panStartY;
              panTotalDx = dx;
              panTotalDy = dy;

              const slide = this.slides[this.currentIndex];
              const img = slide ? this._getImg(slide) : null;
              if (!img) return;

              const displayWidth = img.offsetWidth || 300;
              const displayHeight = img.offsetHeight || 300;
              const boost = 1.8;
              const pctPerPxX = ((100 / this.zoomScale) / displayWidth) * boost;
              const pctPerPxY = ((100 / this.zoomScale) / displayHeight) * boost;

              const nx = Math.max(0, Math.min(100, panStartOriginX - dx * pctPerPxX));
              const ny = Math.max(0, Math.min(100, panStartOriginY - dy * pctPerPxY));
              this._panMoveTo(nx, ny);
            }
          }, { passive: false });

          this.slider.addEventListener('touchend', (e) => {
            // --- DISMISS END ---
            if (dismissLocked) {
              dismissActive = false;
              dismissLocked = false;
              const dy = e.changedTouches[0].clientY - dismissStartY;
              if (dy > 100) {
                // Close with animation
                this.slider.style.transition = 'transform 0.15s ease-out, opacity 0.15s ease-out';
                this.slider.style.transform = `translateY(${window.innerHeight}px) scale(0.5)`;
                this.slider.style.opacity = '0';
                this.style.transition = 'background-color 0.15s ease-out';
                this.style.backgroundColor = 'rgba(0, 0, 0, 0)';
                setTimeout(() => {
                  this.slider.style.transform = '';
                  this.slider.style.transition = '';
                  this.slider.style.opacity = '';
                  this.style.transition = '';
                  this.style.backgroundColor = '';
                  this.slider.style.overflowX = 'auto';
                  this.slider.style.scrollSnapType = 'x mandatory';
                  this.hide();
                }, 160);
              } else {
                // Snap back
                this.slider.style.transition = 'transform 0.15s ease-out';
                this.slider.style.transform = '';
                this.style.transition = 'background-color 0.15s ease-out';
                this.style.backgroundColor = '';
                setTimeout(() => {
                  this.slider.style.transition = '';
                  this.style.transition = '';
                  this.slider.style.overflowX = 'auto';
                  this.slider.style.scrollSnapType = 'x mandatory';
                }, 160);
              }
              return;
            }
            if (dismissActive) { dismissActive = false; }

            // --- PINCH END ---
            if (this._pinching && e.touches.length < 2) {
              this._pinching = false;
              if (this.zoomScale <= 1.05) {
                this.zoomScale = 2.5;
                this.resetZoom();
              } else {
                this.slider.style.overflowX = 'hidden';
                this.slider.style.scrollSnapType = 'none';
                if (this.prevBtn) this.prevBtn.style.display = 'none';
                if (this.nextBtn) this.nextBtn.style.display = 'none';
              }
              return;
            }

            // --- PAN END ---
            if (!this.zoomed || !panActive) { panActive = false; return; }
            panActive = false;

            // Double-tap to unzoom
            const absDx = Math.abs(panTotalDx);
            const absDy = Math.abs(panTotalDy);
            if (absDx < 10 && absDy < 10) {
              const now = Date.now();
              const dt = now - lastZoomedTapTime;
              lastZoomedTapTime = now;
              if (dt < 400 && dt > 0) {
                lastZoomedTapTime = 0;
                this._animateResetZoom();
                return;
              }
            }

            // Edge navigation: only if touch STARTED at the edge and swiped far enough
            if (absDx > 80 && panStartedAtEdge !== 0) {
              if (panStartedAtEdge === -1 && panTotalDx > 0 && this.currentIndex > 0) {
                this._navigateZoomed(this.currentIndex - 1);
                return;
              } else if (panStartedAtEdge === 1 && panTotalDx < 0 && this.currentIndex < this.slides.length - 1) {
                this._navigateZoomed(this.currentIndex + 1);
                return;
              }
            }
            // Momentum coast
            this._startMomentum();
          });
        }

        // IntersectionObserver for slide tracking
        if (this.observer) this.observer.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (this.zoomed || this._navigating) return; // skip during zoom/transition
            for (const entry of entries) {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                const index = this.slides.indexOf(entry.target);
                if (index !== -1) {
                  this.currentIndex = index;
                  this.updateCounter(index);
                  this.updateNavButtons(index);
                  this._preloadAround(index);
                }
              }
            }
          },
          { root: this.slider, threshold: 0.5 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      toggleZoom(clientX, clientY) {
        if (this.zoomed) {
          this._animateResetZoom();
          return;
        }
        this.zoomScale = 2.5;

        const slide = this.slides[this.currentIndex];
        if (!slide) return;
        const img = this._getImg(slide);
        if (!img) return;

        const rect = img.getBoundingClientRect();
        const originX = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        const originY = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));

        img.style.willChange = 'transform';
        img.style.transformOrigin = `${originX}% ${originY}%`;
        img.style.transform = `scale(${this.zoomScale})`;
        img.style.transition = 'transform 0.15s ease-out';
        setTimeout(() => { img.style.transition = ''; }, 160);

        this.zoomed = true;
        this._panOriginX = originX;
        this._panOriginY = originY;

        // Lock slider scroll
        this.slider.style.overflowX = 'hidden';
        this.slider.style.scrollSnapType = 'none';

        // Hide nav while zoomed, show grab cursor
        if (this.prevBtn) this.prevBtn.style.display = 'none';
        if (this.nextBtn) this.nextBtn.style.display = 'none';
        this.slider.style.cursor = 'grab';
      }

      // Animated zoom-out (for double-tap unzoom)
      _animateResetZoom() {
        if (!this.zoomed) return;
        this._stopMomentum();
        const slide = this.slides[this.currentIndex];
        const img = slide ? this._getImg(slide) : null;
        if (img) {
          img.style.transition = 'transform 0.15s ease-out';
          img.style.transform = 'scale(1)';
          setTimeout(() => {
            img.style.transition = '';
            img.style.transform = '';
            img.style.transformOrigin = '';
            img.style.willChange = '';
          }, 160);
        }
        this.zoomScale = 2.5;
        if (this.slider) {
          this.slider.style.overflowX = 'auto';
          this.slider.style.scrollSnapType = 'x mandatory';
        }
        if (this.prevBtn) this.prevBtn.style.display = '';
        if (this.nextBtn) this.nextBtn.style.display = '';
        this.slider.style.cursor = '';
        this._panOriginX = 50;
        this._panOriginY = 50;
        this._velX = 0;
        this._velY = 0;
        this.zoomed = false;
      }

      // Instant reset (for edge navigation, dismiss, etc.)
      resetZoom() {
        if (!this.zoomed) return;
        this._stopMomentum();
        this.slides.forEach((slide) => {
          const img = this._getImg(slide);
          if (img) {
            img.style.transition = '';
            img.style.transform = '';
            img.style.transformOrigin = '';
            img.style.willChange = '';
          }
        });
        this.zoomScale = 2.5;
        if (this.slider) {
          this.slider.style.overflowX = 'auto';
          this.slider.style.scrollSnapType = 'x mandatory';
        }
        if (this.prevBtn) this.prevBtn.style.display = '';
        if (this.nextBtn) this.nextBtn.style.display = '';
        this.slider.style.cursor = '';
        this._panOriginX = 50;
        this._panOriginY = 50;
        this._velX = 0;
        this._velY = 0;
        this.zoomed = false;
      }

      showActiveMedia() {
        if (!this.slider || this.slides.length === 0) return;
        const targetMediaId = this.openedBy?.getAttribute('data-media-id');
        let targetIndex = 0;
        if (targetMediaId) {
          const targetSlide = this.slider.querySelector(`[data-media-id="${targetMediaId}"]`);
          if (targetSlide) {
            targetIndex = this.slides.indexOf(targetSlide);
            if (targetIndex === -1) targetIndex = 0;
          }
        }
        this.navigateTo(targetIndex, false);
        this._preloadAround(targetIndex);
        const activeSlide = this.slides[targetIndex];
        if (activeSlide) {
          const t = activeSlide.querySelector('template');
          const c = t ? t.content : null;
          if (activeSlide.nodeName === 'DEFERRED-MEDIA' && c && c.querySelector('.js-youtube')) {
            activeSlide.loadContent();
          }
        }
      }

      // Eager-load current + adjacent images for instant display
      _preloadAround(index) {
        for (let i = index - 2; i <= index + 2; i++) {
          if (i < 0 || i >= this.slides.length) continue;
          const img = this._getImg(this.slides[i]);
          if (!img) continue;
          if (i === index) {
            img.loading = 'eager';
            img.fetchPriority = 'high';
            img.decoding = 'sync';
          } else {
            img.loading = 'eager';
            img.fetchPriority = 'low';
            img.decoding = 'async';
          }
        }
      }

      navigateTo(index, smooth = true) {
        if (index < 0 || index >= this.slides.length) return;
        if (this.zoomed) this.resetZoom();
        this.currentIndex = index;
        const targetSlide = this.slides[index];
        if (!targetSlide) return;
        this.slider.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: smooth ? 'smooth' : 'instant',
        });
        this.updateCounter(index);
        this.updateNavButtons(index);
      }

      // Navigate while keeping zoom level (for edge-swipe during zoom)
      // Uses translateX on slider for smooth CSS animation (no scroll needed)
      _navigateZoomed(index) {
        if (index < 0 || index >= this.slides.length || this._navigating) return;
        // Reset zoom first, then navigate normally
        this.resetZoom();
        this.navigateTo(index);
      }

      navigateNext() {
        if (this.currentIndex < this.slides.length - 1) this.navigateTo(this.currentIndex + 1);
      }

      navigatePrev() {
        if (this.currentIndex > 0) this.navigateTo(this.currentIndex - 1);
      }

      updateCounter(index) {
        if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
      }

      updateNavButtons(index) {
        if (this.prevBtn) this.prevBtn.disabled = index === 0;
        if (this.nextBtn) this.nextBtn.disabled = index === this.slides.length - 1;
      }
    }
  );
}

